import { CSSProperties, ReactNode, SyntheticEvent } from "react"
import {
  Block,
  Button,
  HeadingXSmall,
  ModalClose,
  useStyletron,
} from "@mezo-org/mezo-clay"

const SLIDE_TRANSITION_MS = 445

export enum SlideMenuDirection {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
}

export type SlideMenuProps = {
  isOpen: boolean
  header?: ReactNode
  // TODO: Define the specific size of the slide up menu. Currently, the
  // component is only used in the staking/liquidity flow.
  height?: string
  onClose: (e: SyntheticEvent<HTMLButtonElement>) => void
  style?: CSSProperties
  children: ReactNode
  closable?: boolean
  direction?: SlideMenuDirection
}

const getSlideMenuTransform = (
  direction: SlideMenuDirection,
  isOpen: boolean,
): string => {
  if (isOpen) return "translate(0, 0)"

  switch (direction) {
    case SlideMenuDirection.TOP:
      return "translateY(-100%)"
    case SlideMenuDirection.BOTTOM:
      return "translateY(100%)"
    case SlideMenuDirection.LEFT:
      return "translateX(-100%)"
    case SlideMenuDirection.RIGHT:
      return "translateX(100%)"
    default:
      return "translateY(100%)"
  }
}

const getSlideMenuPositionStyles = (
  direction: SlideMenuDirection,
  height: string,
) => {
  switch (direction) {
    case SlideMenuDirection.TOP:
      return { top: "0px", left: "0px", right: "0px", height }
    case SlideMenuDirection.BOTTOM:
      return { bottom: "0px", left: "0px", right: "0px", height }
    case SlideMenuDirection.LEFT:
      return { left: "0px", top: "0px", bottom: "0px", width: "100%" }
    case SlideMenuDirection.RIGHT:
      return { right: "0px", top: "0px", bottom: "0px", width: "100%" }
    default:
      return { bottom: "0px", left: "0px", right: "0px", height }
  }
}

export default function SlideMenu({
  isOpen,
  header,
  height = "auto",
  onClose,
  style,
  children,
  closable = true,
  direction = SlideMenuDirection.BOTTOM,
}: SlideMenuProps) {
  const [css, theme] = useStyletron()

  return (
    <>
      <Block
        className={`${css({
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          zIndex: 998,
          background: theme.colors.contentPrimary,
          opacity: 0.6,
          transition: `opacity cubic-bezier(0.19, 1, 0.22, 1) ${SLIDE_TRANSITION_MS}ms, visibility ${SLIDE_TRANSITION_MS}ms`,
        })} ${
          !isOpen &&
          css({
            opacity: 0,
            visibility: "hidden",
            pointerEvents: "none",
          })
        }`}
      />
      <Block
        className={`${css({
          ...getSlideMenuPositionStyles(direction, height),
          borderRadius: theme.sizing.scale600,
          overflow: "hidden",
          background: theme.colors.primaryB,
          position: "fixed",
          zIndex: 999,
          transform: getSlideMenuTransform(direction, isOpen),
          opacity: 1,
          transition: `transform cubic-bezier(0.19, 1, 0.22, 1) ${SLIDE_TRANSITION_MS}ms`,
          boxSizing: "border-box",
        })} ${
          !isOpen &&
          css({
            transition: `transform cubic-bezier(0.19, 1, 0.22, 1) ${SLIDE_TRANSITION_MS}ms, opacity 0ms ${SLIDE_TRANSITION_MS}ms`,
            opacity: 0,
            pointerEvents: "none",
          })
        }`}
        style={style}
      >
        <Block
          padding={theme.sizing.scale800}
          paddingBottom={theme.sizing.scale950}
        >
          {typeof header === "string" ? (
            <HeadingXSmall
              className={css({
                textAlign: "center",
              })}
            >
              {header}
            </HeadingXSmall>
          ) : (
            header
          )}
        </Block>
        {closable && (
          <Button
            $as={ModalClose}
            onClick={onClose}
            overrides={{ BaseButton: { component: ModalClose } }}
          />
        )}
        {children}
      </Block>
    </>
  )
}
